import React from "react";
import { Form } from "react-bootstrap";

interface RatingScaleOption {
  code: string;
  label: string;
}

interface Props {
  itemType: string;
  options?: string[] | null; // For checkbox-group
  ratingScaleOptions?: RatingScaleOption[]; // For rating
  value: string | null;
  onChange: (value: string | null) => void;
  compact?: boolean; // For bulk grid mode
}

/**
 * Renders the appropriate input control based on item type.
 */
const ItemInputFactory: React.FC<Props> = ({
  itemType,
  options,
  ratingScaleOptions,
  value,
  onChange,
  compact,
}) => {
  switch (itemType) {
    case "rating":
      return (
        <RatingInput
          options={ratingScaleOptions || []}
          value={value}
          onChange={onChange}
          compact={compact}
        />
      );
    case "checkbox-group":
      return (
        <CheckboxGroupInput
          options={options || []}
          value={value}
          onChange={onChange}
          compact={compact}
        />
      );
    case "numeric":
      return (
        <Form.Control
          type="number"
          size="sm"
          style={{ width: compact ? 80 : 120 }}
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="0"
        />
      );
    case "text":
      return compact ? (
        <Form.Control
          size="sm"
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="Enter remarks..."
        />
      ) : (
        <Form.Control
          as="textarea"
          size="sm"
          rows={2}
          value={value || ""}
          onChange={(e) => onChange(e.target.value || null)}
          placeholder="Enter remarks..."
        />
      );
    default:
      return <span className="text-muted">Unknown type: {itemType}</span>;
  }
};

// --- Rating Input ---
const RatingInput: React.FC<{
  options: RatingScaleOption[];
  value: string | null;
  onChange: (v: string | null) => void;
  compact?: boolean;
}> = ({ options, value, onChange, compact }) => {
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      {options.map((opt) => (
        <button
          key={opt.code}
          type="button"
          onClick={() => onChange(value === opt.code ? null : opt.code)}
          style={{
            padding: compact ? "2px 8px" : "4px 12px",
            fontSize: compact ? 11 : 13,
            border:
              value === opt.code
                ? "2px solid #0d6efd"
                : "1px solid #ced4da",
            borderRadius: 4,
            background: value === opt.code ? "#0d6efd" : "#fff",
            color: value === opt.code ? "#fff" : "#333",
            cursor: "pointer",
            fontWeight: value === opt.code ? 600 : 400,
          }}
          title={opt.label}
        >
          {opt.code}
        </button>
      ))}
    </div>
  );
};

// --- Checkbox Group Input ---
const CheckboxGroupInput: React.FC<{
  options: string[];
  value: string | null;
  onChange: (v: string | null) => void;
  compact?: boolean;
}> = ({ options, value, onChange, compact }) => {
  let checked: string[] = [];
  try {
    checked = value ? JSON.parse(value) : [];
  } catch {
    checked = [];
  }

  const toggle = (opt: string) => {
    const next = checked.includes(opt)
      ? checked.filter((c) => c !== opt)
      : [...checked, opt];
    onChange(next.length > 0 ? JSON.stringify(next) : null);
  };

  return (
    <div
      style={{
        display: "flex",
        gap: compact ? 4 : 6,
        flexWrap: "wrap",
        maxWidth: compact ? 200 : undefined,
      }}
    >
      {options.map((opt) => (
        <label
          key={opt}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
            fontSize: compact ? 11 : 13,
            cursor: "pointer",
            padding: "1px 4px",
            borderRadius: 3,
            background: checked.includes(opt) ? "#d4edda" : "transparent",
          }}
        >
          <input
            type="checkbox"
            checked={checked.includes(opt)}
            onChange={() => toggle(opt)}
            style={{ width: 14, height: 14 }}
          />
          {opt}
        </label>
      ))}
    </div>
  );
};

export default ItemInputFactory;
